import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { PHONE_TRACKS } from "../data/curated";
import { ScrollText } from "./ScrollText";
import { useRailLoop } from "./useRailLoop";

const THUMB_W = 88; // px — covers stay this size, phone takes the rest
const VISIBLE = 4; // cover slots shown at once
const CAPTION_H = 22; // song title + artist text under each cover
const BADGE_H = 8; // now-playing dot row under the caption
const SLOT_H = THUMB_W + CAPTION_H + BADGE_H;

// Pull a vibrant tint color out of a cover image (for the playing glow)
function coverTint(src) {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            try {
                const c = document.createElement("canvas");
                c.width = 24;
                c.height = 24;
                const ctx = c.getContext("2d");
                ctx.drawImage(img, 0, 0, 24, 24);
                const d = ctx.getImageData(0, 0, 24, 24).data;
                let best = null;
                let bestScore = -1;
                let sr = 0;
                let sg = 0;
                let sb = 0;
                const n = d.length / 4;
                for (let i = 0; i < d.length; i += 4) {
                    const r = d[i];
                    const g = d[i + 1];
                    const b = d[i + 2];
                    const mx = Math.max(r, g, b);
                    const mn = Math.min(r, g, b);
                    const lum = (r + g + b) / 3;
                    const score =
                        (mx === 0 ? 0 : (mx - mn) / mx) *
                        (lum > 40 ? 1 : 0.3);
                    sr += r;
                    sg += g;
                    sb += b;
                    if (score > bestScore) {
                        bestScore = score;
                        best = [r, g, b];
                    }
                }
                const rgb = best
                    ? best.map((v, k) =>
                          Math.round(v * 0.65 + ([sr, sg, sb][k] / n) * 0.35)
                      )
                    : [
                          Math.round(sr / n),
                          Math.round(sg / n),
                          Math.round(sb / n),
                      ];
                resolve(rgb.join(","));
            } catch {
                resolve(null);
            }
        };
        img.onerror = () => resolve(null);
        img.src = src;
    });
}

// Depth helpers for the solid play/pause circle: a lighter top + darker
// bottom gradient over the cover's dominant color, with the frame ring a
// shade darker than the fill.
const tintLighten = (rgb, amt) =>
    rgb
        .split(",")
        .map((v) => Math.round(Number(v) + (255 - Number(v)) * amt))
        .join(",");
const tintDarken = (rgb, f) =>
    rgb
        .split(",")
        .map((v) => Math.round(Number(v) * f))
        .join(",");

// iPhone / iPod / iPad (incl. iPadOS desktop-UA): the <audio> element must
// stay NATIVE there — routing it through Web Audio kills lock-screen +
// background playback (iOS suspends the graph the moment the screen locks).
const appleMobile = () =>
    typeof navigator !== "undefined" &&
    (/iP(hone|od|ad)/.test(navigator.userAgent) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));

// ---- Offline beat envelope (Apple mobile) --------------------------------
// iOS suspends Web Audio graphs in the background, so the element stays
// native there. To still sync the bars to the REAL music, the track is
// decoded once and its per-band energy envelope is precomputed offline;
// during playback the bars sample the envelope at audio.currentTime.
const EQ_HOP = 0.1; // seconds per envelope frame
const EQ_BANDS_HZ = [
    [0, 688],
    [688, 1720],
    [1720, 3440],
    [3440, 6190],
    [6190, 12380],
];
const EQ_GAINS = [1.0, 1.15, 1.3, 1.5, 1.75];
const eqEnvelopeCache = new Map();

function fft(re, im) {
    const n = re.length;
    for (let i = 1, j = 0; i < n; i++) {
        let bit = n >> 1;
        for (; j & bit; bit >>= 1) j ^= bit;
        j ^= bit;
        if (i < j) {
            const tr = re[i];
            re[i] = re[j];
            re[j] = tr;
            const ti = im[i];
            im[i] = im[j];
            im[j] = ti;
        }
    }
    for (let len = 2; len <= n; len <<= 1) {
        const ang = (-2 * Math.PI) / len;
        const wr = Math.cos(ang);
        const wi = Math.sin(ang);
        for (let i = 0; i < n; i += len) {
            let cr = 1;
            let ci = 0;
            for (let k = 0; k < len / 2; k++) {
                const ur = re[i + k];
                const ui = im[i + k];
                const pr = re[i + k + len / 2];
                const pi = im[i + k + len / 2];
                const vr = pr * cr - pi * ci;
                const vi = pr * ci + pi * cr;
                re[i + k] = ur + vr;
                im[i + k] = ui + vi;
                re[i + k + len / 2] = ur - vr;
                im[i + k + len / 2] = ui - vi;
                const ncr = cr * wr - ci * wi;
                ci = cr * wi + ci * wr;
                cr = ncr;
            }
        }
    }
}

async function buildEqEnvelope(track) {
    const res = await fetch(track.src);
    const buf = await res.arrayBuffer();
    const OfflineCtx =
        window.OfflineAudioContext || window.webkitOfflineAudioContext;
    const off = new OfflineCtx(1, 1, 44100); // decode-only context
    const audio = await off.decodeAudioData(buf);
    const sr = audio.sampleRate;
    const N = 1024;
    const hop = Math.round(sr * EQ_HOP);
    const frames = Math.max(1, Math.floor((audio.length - N) / hop) + 1);
    const binHz = sr / N;
    const bandBins = EQ_BANDS_HZ.map(([a, b]) => [
        Math.max(1, Math.round(a / binHz)),
        Math.max(2, Math.round(b / binHz)),
    ]);
    const chan = audio.getChannelData(0);
    const bands = EQ_BANDS_HZ.map(() => new Float32Array(frames));
    const re = new Float32Array(N);
    const im = new Float32Array(N);
    // Yield every 60 frames so the main thread (and the music) stays smooth
    const yieldEvery = 60;
    for (let f = 0; f < frames; f++) {
        const off0 = f * hop;
        for (let i = 0; i < N; i++) {
            re[i] =
                (chan[off0 + i] || 0) *
                (0.5 - 0.5 * Math.cos((2 * Math.PI * i) / N));
            im[i] = 0;
        }
        fft(re, im);
        for (let bi = 0; bi < bandBins.length; bi++) {
            const [a, b] = bandBins[bi];
            let s = 0;
            for (let k = a; k < Math.min(b, N / 2); k++)
                s += Math.sqrt(re[k] * re[k] + im[k] * im[k]);
            bands[bi][f] = (s / Math.max(1, b - a)) * EQ_GAINS[bi];
        }
        if (f % yieldEvery === yieldEvery - 1)
            await new Promise((r) => setTimeout(r, 0));
    }
    // Map each band onto a dB scale, anchored at its own 97th percentile
    // with a window that adapts to that band's real dynamics (clamped
    // 30-65dB): dense bass gets a tight window so it sways with the kick,
    // spiky treble gets a wide one so cymbal hits still read.
    bands.forEach((arr) => {
        for (let f = 0; f < arr.length; f++)
            arr[f] = 20 * Math.log10(Math.max(arr[f], 1e-9));
        const sorted = Array.from(arr).sort((x, y) => x - y);
        const top = sorted[Math.floor(sorted.length * 0.97)] || 0;
        const bottom = sorted[Math.floor(sorted.length * 0.05)] || top - 40;
        const win = Math.min(65, Math.max(30, top - bottom));
        for (let f = 0; f < arr.length; f++)
            arr[f] = Math.min(1, Math.max(0, (arr[f] - (top - win)) / win));
    });
    // Perceptual tilt AFTER the dB mapping (like the live analyser's gain):
    // ears are less sensitive to highs, so treble bands get boosted to read
    // as loudly as the bass does. No clamp — the AGC needs headroom.
    const POST = [1.0, 1.1, 1.3, 1.7, 2.2];
    bands.forEach((arr, bi) => {
        for (let f = 0; f < arr.length; f++) arr[f] = arr[f] * POST[bi];
    });
    return { bands };
}

export default function PhoneAudioSection() {
    // viewIdx = which artist card the phone shows; playIdx = which song the
    // audio element holds. They are decoupled on purpose: browsing other
    // artists never interrupts the song that is playing.
    const [viewIdx, setViewIdx] = useState(0);
    const [playIdx, setPlayIdx] = useState(null);
    const audRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState({ t: 0, d: 0 });
    const pendingPlay = useRef(false);
    // Deep-link ?autoplay=1 starts the song MUTED (muted autoplay is allowed
    // without a user gesture); it is unmuted when the intro ends / on first tap.
    const autoStartMuted = useRef(false);
    const frameRef = useRef(null);
    const [railH, setRailH] = useState(null);
    const [glowRgb, setGlowRgb] = useState("3,135,226");
    const glowCache = useRef({});
    // Live beat sync plumbing (Web Audio analyser drives the mini bars)
    const audioCtxRef = useRef(null);
    const analyserRef = useRef(null);
    const sourceTriedRef = useRef(new WeakSet());
    const barsRef = useRef([]);
    const rafRef = useRef(0);
    // Offline beat envelope (Apple mobile — element stays native)
    const eqEnvRef = useRef(null);
    // AGC rolling peak (keeps headroom during loud passages)
    const agcRef = useRef(0.9);
    // Rail auto-scroll: brief pause on thumbnail selection, auto-resumes
    // after 5s without needing another touch
    const [railPaused, setRailPaused] = useState(false);
    const railTimerRef = useRef(0);
    const pauseRailBriefly = () => {
        setRailPaused(true);
        clearTimeout(railTimerRef.current);
        railTimerRef.current = setTimeout(() => setRailPaused(false), 5000);
    };

    const view = PHONE_TRACKS[viewIdx];
    const playing = playIdx == null ? null : PHONE_TRACKS[playIdx];
    // PlayerOpen LATCHES the full-screen Now Playing view: pausing keeps
    // it on screen (the circle flips to PLAY), and the only way back to
    // the artist card is the RETURN pill.
    const [playerOpen, setPlayerOpen] = useState(false);
    const showingPlaying = playerOpen && playIdx === viewIdx;
    // Solid PAUSE circle belongs ONLY to the card whose song is playing —
    // browsing other artists keeps their default pulsing PLAY.
    const thisCardPlaying = isPlaying && playIdx === viewIdx;

    const loop = PHONE_TRACKS.length > VISIBLE;
    const visibleCount = Math.min(PHONE_TRACKS.length, VISIBLE);
    const strip = loop ? [...PHONE_TRACKS, ...PHONE_TRACKS] : PHONE_TRACKS;
    const railRef = useRef(null);
    useRailLoop(railRef, loop, PHONE_TRACKS.length * 4, railPaused);

    // Stretch the rail window to sit 10px inside the phone top/bottom
    // (cover size stays fixed; only the spacing flexes). All widths —
    // the desktop layout reuses this phone mockup side by side.
    useLayoutEffect(() => {
        const measure = () => {
            const h = frameRef.current?.getBoundingClientRect().height ?? 0;
            setRailH(
                h >= VISIBLE * SLOT_H + (VISIBLE - 1) * 6
                    ? Math.round(h) - 35
                    : null
            );
        };
        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, []);
    const railGap = railH
        ? (railH - VISIBLE * SLOT_H) / (VISIBLE - 1)
        : 10;
    // tightened air between rail slots (2px — matches the tuned static floor)
    const thumbGap = 2;

    // Preload every artist image so switching is instant
    useEffect(() => {
        PHONE_TRACKS.forEach((m) => {
            const i = new Image();
            i.src = m.artistImage;
        });
    }, []);

    // Route the <audio> element through an AnalyserNode so the mini bars can
    // dance to the real spectrum. Created lazily inside the play tap (the
    // AudioContext must wake inside a user gesture); one MediaElementSource
    // per element (the element remounts for every track change).
    const ensureAudio = (el) => {
        // Apple mobile: keep the <audio> element native — lock screen +
        // background playback win; the bars ride the organic idle there.
        if (appleMobile()) return;
        try {
            if (!audioCtxRef.current) {
                const Ctx = window.AudioContext || window.webkitAudioContext;
                if (!Ctx) return;
                audioCtxRef.current = new Ctx();
                analyserRef.current = audioCtxRef.current.createAnalyser();
                analyserRef.current.fftSize = 128;
                analyserRef.current.smoothingTimeConstant = 0.75;
                analyserRef.current.connect(audioCtxRef.current.destination);
            }
            const ctx = audioCtxRef.current;
            // Wake the context inside the tap — strict browsers (Safari/iOS)
            // refuse resume() calls made outside the starting gesture.
            if (ctx.state === "suspended") ctx.resume().catch(() => {});
            if (el && !sourceTriedRef.current.has(el)) {
                sourceTriedRef.current.add(el);
                ctx.createMediaElementSource(el).connect(analyserRef.current);
            }
        } catch {
            /* analyser unavailable — bars fall back to organic idle motion */
        }
    };

    const startPlay = (i) => {
        if (i === playIdx) {
            const a = audRef.current;
            if (!a) return;
            if (a.paused) {
                ensureAudio(a);
                a.play().catch(() => {});
            } else a.pause();
        } else {
            // Big circle = start/stop ONLY — it never opens the full-screen
            // player. The mini-player's left zone is the only way in.
            // First play / switching songs: wake the context IN this tap —
            // the <audio> element itself only mounts after the state update.
            ensureAudio(null);
            setPlayIdx(i);
            pendingPlay.current = true;
        }
    };

    // Start the pending song once its source is loaded. useLayoutEffect
    // deliberately: it runs in the SAME task as the play tap, which iOS
    // Safari requires before it will allow element.play().
    useLayoutEffect(() => {
        if (playIdx == null) return;
        const a = audRef.current;
        if (pendingPlay.current && a) {
            if (autoStartMuted.current) {
                // Native element, started muted — skip the Web Audio graph so
                // the muted→unmute trick isn't blocked by a suspended context.
                // Reset immediately after use: any subsequent song the user
                // explicitly picks must play with full sound, not stay muted.
                a.muted = true;
                a.play().catch(() => {});
                autoStartMuted.current = false;
            } else {
                // Explicitly unmute in case the element inherited muted=true
                // from a prior shared-link autoplay session.
                a.muted = false;
                ensureAudio(a);
                a.play().catch(() => {});
            }
            pendingPlay.current = false;
        }
    }, [playIdx]);

    // iOS unlock: the very first touch anywhere wakes the audio context
    // (Safari refuses context.resume() outside a direct user touch).
    useEffect(() => {
        const unlock = () => ensureAudio(audRef.current);
        document.addEventListener("touchend", unlock, { once: true });
        document.addEventListener("click", unlock, { once: true });
        return () => {
            document.removeEventListener("touchend", unlock);
            document.removeEventListener("click", unlock);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Build the offline beat envelope for Apple mobile (native element)
    useEffect(() => {
        if (!playing || !appleMobile()) return;
        const cached = eqEnvelopeCache.get(playing.id);
        if (cached) {
            eqEnvRef.current = cached;
            return;
        }
        eqEnvRef.current = null;
        let dead = false;
        buildEqEnvelope(playing)
            .then((env) => {
                if (!dead) {
                    eqEnvelopeCache.set(playing.id, env);
                    eqEnvRef.current = env;
                }
            })
            .catch(() => {});
        return () => {
            dead = true;
        };
    }, [playing]);

    // Drive the mini-player bars from the live frequency spectrum, blended
    // with a gentle organic idle so bars are never frozen — and the canned
    // CSS wave is gone entirely. Real music dominates the moment it flows.
    useEffect(() => {
        if (!isPlaying) return;
        const an = analyserRef.current;
        const ctx = audioCtxRef.current;
        const data = an ? new Uint8Array(an.frequencyBinCount) : null;
        const cur = [0.18, 0.18, 0.18, 0.18, 0.18];
        let zeroStreak = 0;
        // Five spectrum bands, bass -> highs (bin ranges at 44.1kHz/128fft =
        // 344Hz per bin), with a gentle tilt gain so the treble bands move
        // as visibly as the bass — hi-hat energy alone is much quieter.
        const BANDS = [
            [0, 2, 1.0],
            [2, 5, 1.15],
            [5, 10, 1.3],
            [10, 18, 1.5],
            [18, 36, 1.75],
        ];
        const tick = () => {
            rafRef.current = requestAnimationFrame(tick);
            // A suspended context reads frozen zeros — keep nudging it awake
            if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {});
            let vals = null;
            if (an && data) {
                an.getByteFrequencyData(data);
                const band = (a, b, g) => {
                    let s = 0;
                    for (let i = a; i < b; i++) s += data[i];
                    // no hard clamp here — the AGC below needs headroom
                    return (s / Math.max(1, b - a) / 255) * g;
                };
                vals = BANDS.map(([a, b, g]) => band(a, b, g));
                const sum = vals.reduce((x, y) => x + y, 0);
                if (sum <= 0.001) zeroStreak += 1;
                else zeroStreak = 0;
                // A connected-but-sourceless analyser reads zeros forever —
                // fall through to the offline envelope when that happens.
                if (zeroStreak > 45) vals = null;
            }
            if (!vals && eqEnvRef.current) {
                // Apple mobile: sample the precomputed envelope at the song's
                // current time — true beat response with a native element.
                const a = audRef.current;
                const t = a ? a.currentTime : 0;
                const fi = Math.floor(t / EQ_HOP);
                vals = eqEnvRef.current.bands.map(
                    (arr) =>
                        arr[Math.min(arr.length - 1, Math.max(0, fi))] || 0
                );
            }
            const t = performance.now() / 1000;
            // AGC: rolling peak with fast attack / slow release keeps
            // headroom, so loud passages never pin every bar flat.
            let scale = 1;
            if (vals) {
                const framePeak = Math.max(...vals);
                agcRef.current =
                    framePeak > agcRef.current
                        ? agcRef.current + (framePeak - agcRef.current) * 0.5
                        : agcRef.current + (framePeak - agcRef.current) * 0.02;
                agcRef.current = Math.min(
                    1.35,
                    Math.max(0.55, agcRef.current)
                );
                scale = 1 / agcRef.current;
            }
            barsRef.current.forEach((el, i) => {
                if (!el) return;
                const live = vals
                    ? Math.min(0.95, vals[i] * scale)
                    : 0;
                const idle =
                    (0.14 +
                        0.08 *
                            (0.5 + 0.5 * Math.sin(t * 2.2 + i * 1.9)) *
                            (0.6 + 0.4 * Math.sin(t * 0.9 + i))) *
                    (vals ? 0.75 : 1);
                const target = Math.max(live, idle);
                cur[i] += (target - cur[i]) * 0.35;
                el.style.transform = `scaleY(${cur[i].toFixed(3)})`;
            });
        };
        rafRef.current = requestAnimationFrame(tick);
        return () => {
            cancelAnimationFrame(rafRef.current);
            barsRef.current.forEach((el) => {
                if (el) el.style.transform = "scaleY(0.18)";
            });
        };
    }, [isPlaying]);

    const skip = (dir) => {
        if (playIdx == null) {
            startPlay(viewIdx);
            return;
        }
        const ni =
            (playIdx + dir + PHONE_TRACKS.length) % PHONE_TRACKS.length;
        setPlayIdx(ni);
        setViewIdx(ni);
        pendingPlay.current = isPlaying || pendingPlay.current;
    };

    const fmt = (s) => {
        if (!Number.isFinite(s)) return "0:00";
        const m = Math.floor(s / 60);
        return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
    };

    const seekFromEvent = (e) => {
        const a = audRef.current;
        if (!a || !Number.isFinite(a.duration)) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const pct = Math.min(
            Math.max((e.clientX - rect.left) / rect.width, 0),
            1
        );
        a.currentTime = pct * a.duration;
        setProgress({ t: pct * a.duration, d: a.duration });
    };

    const share = async () => {
        const t = PHONE_TRACKS[viewIdx];
        const url = `${window.location.origin}/api/share/track/${t.id}`;
        try {
            if (navigator.share) {
                await navigator.share({
                    title: `${t.title} — ${t.artist}`,
                    url,
                });
                return;
            }
            throw new Error("no share api");
        } catch (err) {
            if (err && err.name === "AbortError") return;
            try {
                await navigator.clipboard.writeText(url);
                toast("Link copied to clipboard");
            } catch {
                toast("Couldn't share this song");
            }
        }
    };

    // Deep link: ?track=<song-id> opens that artist card. With &autoplay=1
    // (links shared from the Share button) it also opens the Now Playing
    // view and starts the song. Mobile browsers block autoplay-with-sound on
    // a fresh page load, so we also arm the first tap/click to start it.
    const initialTrackId = useRef(
        new URLSearchParams(window.location.search).get("track")
    );
    const initialAutoplay = useRef(
        new URLSearchParams(window.location.search).get("autoplay") === "1"
    );
    useEffect(() => {
        const i = PHONE_TRACKS.findIndex(
            (t) => t.id === initialTrackId.current
        );
        if (i < 0) return;
        setViewIdx(i);
        if (!initialAutoplay.current) return;
        setPlayerOpen(true);
        setPlayIdx(i);
        autoStartMuted.current = true;
        pendingPlay.current = true; // layout effect starts it MUTED
        // Unmute for real sound. On the intro ending (timer, no gesture) we
        // keep the element NATIVE — do NOT route it into the Web Audio graph,
        // whose context can't resume without a gesture and would cut the
        // desktop sound. On the first real tap we also enable the EQ graph.
        // iOS Safari refuses unmute without a gesture, so the first tap is the
        // reliable fallback there.
        const unmuteNative = () => {
            const a = audRef.current;
            if (!a) return;
            a.muted = false;
            a.volume = 1;
            if (a.paused) a.play().catch(() => {});
        };
        const unmuteWithGesture = () => {
            unmuteNative();
            ensureAudio(audRef.current); // gesture → context resumes, EQ lives
        };
        window.addEventListener("rivvr:introdone", unmuteNative);
        window.addEventListener("pointerdown", unmuteWithGesture, { once: true });
        window.addEventListener("touchend", unmuteWithGesture, { once: true });
        return () => {
            window.removeEventListener("rivvr:introdone", unmuteNative);
            window.removeEventListener("pointerdown", unmuteWithGesture);
            window.removeEventListener("touchend", unmuteWithGesture);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Artwork color sync: the dominant color of the cover the controls
    // refer to — the loaded song's cover while one is loaded (playing or
    // paused), otherwise the artist card the phone is showing. Feeds the
    // phone glow, the mini-player pulse and the play/pause circles.
    useEffect(() => {
        const idx = playIdx != null ? playIdx : viewIdx;
        const m = PHONE_TRACKS[idx];
        if (!m) return;
        if (glowCache.current[m.id]) {
            setGlowRgb(glowCache.current[m.id]);
            return;
        }
        let dead = false;
        coverTint(m.cover).then((rgb) => {
            if (!dead && rgb) {
                glowCache.current[m.id] = rgb;
                setGlowRgb(rgb);
            }
        });
        return () => {
            dead = true;
        };
    }, [playIdx, viewIdx]);

    // Lock Screen controls + artwork (Media Session)
    useEffect(() => {
        if (!("mediaSession" in navigator) || !playing) return;
        navigator.mediaSession.metadata = new window.MediaMetadata({
            title: playing.title,
            artist: playing.artist,
            album: playing.meta,
            artwork: [
                {
                    src: playing.playingArt,
                    sizes: "921x2000",
                    type: "image/webp",
                },
            ],
        });
        navigator.mediaSession.setActionHandler("play", () =>
            audRef.current?.play().catch(() => {})
        );
        navigator.mediaSession.setActionHandler("pause", () =>
            audRef.current?.pause()
        );
        navigator.mediaSession.setActionHandler("seekto", (d) => {
            if (d.seekTime != null && audRef.current)
                audRef.current.currentTime = d.seekTime;
        });
        navigator.mediaSession.setActionHandler("previoustrack", () =>
            skip(-1)
        );
        navigator.mediaSession.setActionHandler("nexttrack", () => skip(1));
        navigator.mediaSession.playbackState = isPlaying
            ? "playing"
            : "paused";
    }, [playing, isPlaying]);

    // Pause this song when a movie starts playing anywhere on the page
    useEffect(() => {
        const pauseOnVideo = (e) => {
            if (e.detail?.source === "video") {
                const a = audRef.current;
                if (a && !a.paused) a.pause();
            }
        };
        window.addEventListener("rivvr:media-play", pauseOnVideo);
        return () =>
            window.removeEventListener("rivvr:media-play", pauseOnVideo);
    }, []);

    return (
        <div
            className="overflow-x-clip px-4"
            data-testid="phone-audio-section"
        >
            <div className="-mx-4 mx-auto flex max-w-[430px] flex-col md:[zoom:0.819] lg:[zoom:0.9391] xl:[zoom:1.0156] 2xl:[zoom:1.05]">
                <div
                    className="-mb-2 -translate-y-[6px] max-sm:-translate-x-[50px] flex flex-col items-start gap-1 sm:mb-4 sm:translate-x-[6px] sm:-translate-y-[6px] md:mb-6 md:mx-auto md:w-[calc(100%-96px)] md:-translate-y-0 md:-translate-x-[52px]"
                    data-testid="audio-header"
                >
                    <h3 className="shrink-0 max-sm:translate-x-[115px] sm:translate-x-[75px] font-display text-[clamp(1.5rem,6vw,2rem)] font-bold leading-tight tracking-tight text-ivory md:text-[clamp(1.5rem,6vw,34px)]">
                        <span className="text-neon">RIVVR</span>{" "}
                        <span className="italic">Music</span>
                    </h3>
                    <div className="flex flex-col gap-0 text-[16px] md:text-[17px] lg:text-[17px] xl:text-[16px] 2xl:text-[17px] leading-[1.2] text-[#8CC8FF] -translate-y-[4px] max-sm:translate-x-[50px] sm:translate-x-[20px]">
                        <span className="whitespace-nowrap sm:-translate-x-[1px]">• Founded by Musicians &amp; Producers •</span>
                        <span className="whitespace-nowrap">• Streaming Artists to be discovered •</span>
                    </div>
                </div>
                <div className="-mt-[20px] max-sm:-mt-[15px] flex items-center gap-2">
                <div className="relative min-w-0 flex-1 max-sm:scale-[0.94] max-sm:-translate-x-1">
                    <div
                        ref={frameRef}
                        className="relative mx-auto w-full rounded-[2.6rem] border border-white/10 bg-coal p-[7px] shadow-[0_40px_100px_-30px_rgba(0,0,0,0.9),0_0_70px_-20px_rgba(3,135,226,0.25)]"
                    >
                        <div
                            className="relative aspect-[9/19.95] md:aspect-[9/19.2] overflow-hidden rounded-[2.1rem] bg-void [-webkit-mask-image:radial-gradient(#fff,#fff)] [mask-image:radial-gradient(#fff,#fff)]"
                            data-testid="song-screen"
                        >
                            <img
                                src={view.artistImage}
                                alt={view.title}
                                data-testid="song-screen-art"
                                className="absolute inset-0 h-full w-full scale-[1.01] rounded-[2.1rem] object-cover"
                            />
                            {playing && (
                                <img
                                    src={playing.playingArt}
                                    alt=""
                                    aria-hidden
                                    data-testid="song-playing-art"
                                    className={`absolute inset-0 z-10 h-full w-full scale-[1.01] rounded-[2.1rem] object-cover transition-opacity duration-700 ${
                                        showingPlaying
                                            ? "opacity-100"
                                            : "opacity-0"
                                    }`}
                                />
                            )}
                            {playing && (
                                <audio
                                    ref={audRef}
                                    data-testid="song-audio"
                                    crossOrigin="anonymous"
                                    preload="none"
                                    src={playing.src}
                                    onPlay={() => {
                                        setIsPlaying(true);
                                        window.dispatchEvent(
                                            new CustomEvent(
                                                "rivvr:media-play",
                                                {
                                                    detail: {
                                                        source: "audio",
                                                    },
                                                }
                                            )
                                        );
                                    }}
                                    onPause={() => setIsPlaying(false)}
                                    onEnded={() => {
                                        setIsPlaying(false);
                                        if (audRef.current)
                                            audRef.current.currentTime = 0;
                                    }}
                                    onLoadedMetadata={(e) =>
                                        setProgress({
                                            t: 0,
                                            d: e.target.duration,
                                        })
                                    }
                                    onTimeUpdate={(e) => {
                                        setProgress({
                                            t: e.target.currentTime,
                                            d: e.target.duration,
                                        });
                                        // Lock-screen scrubber position
                                        if ("mediaSession" in navigator) {
                                            try {
                                                navigator.mediaSession.setPositionState(
                                                    {
                                                        duration:
                                                            e.target.duration,
                                                        position:
                                                            e.target.currentTime,
                                                        playbackRate:
                                                            e.target.playbackRate,
                                                    }
                                                );
                                            } catch {}
                                        }
                                    }}
                                />
                            )}
                            {/* live overlays aligned over the baked Now Playing UI */}
                            <div
                                className={`absolute inset-0 z-10 transition-opacity duration-300 ${
                                    showingPlaying
                                        ? "opacity-100"
                                        : "pointer-events-none opacity-0"
                                }`}
                                data-testid="song-nowplaying-ui"
                            >
                                <div
                                    className="absolute left-[4.5%] right-[4.5%] flex h-6 cursor-pointer items-center"
                                    style={{ top: "67.6%" }}
                                    onPointerDown={seekFromEvent}
                                    onPointerMove={(e) => {
                                        if (e.buttons === 1)
                                            seekFromEvent(e);
                                    }}
                                    data-testid="song-seek-bar"
                                >
                                    <div className="relative h-[3px] w-full rounded-full bg-white/25">
                                        <div
                                            className="absolute inset-y-0 left-0 rounded-full bg-white/90"
                                            style={{
                                                width: `${
                                                    progress.d
                                                        ? (progress.t /
                                                            progress.d) *
                                                          100
                                                        : 0
                                                }%`,
                                            }}
                                        />
                                        <div
                                            className="absolute top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white shadow"
                                            style={{
                                                left: `${
                                                    progress.d
                                                        ? (progress.t /
                                                            progress.d) *
                                                          100
                                                        : 0
                                                }%`,
                                            }}
                                        />
                                    </div>
                                </div>
                                <div
                                    className="absolute left-[4.5%] text-[10px] font-medium text-white/60 tabular-nums"
                                    style={{ top: "71.2%" }}
                                    data-testid="song-time-elapsed"
                                >
                                    {fmt(progress.t)}
                                </div>
                                <div
                                    className="absolute right-[4.5%] text-right text-[10px] font-medium text-white/60 tabular-nums"
                                    style={{ top: "71.2%" }}
                                    data-testid="song-time-remaining"
                                >
                                    {fmt(
                                        Math.max(
                                            (progress.d || 0) - progress.t,
                                            0
                                        )
                                    )}
                                </div>
                                <button
                                    data-testid="song-prev-btn"
                                    aria-label="Previous song"
                                    onClick={() => skip(-1)}
                                    className="absolute z-20 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center"
                                    style={{
                                        left: "23.7%",
                                        top: "79.6%",
                                    }}
                                />
                                <button
                                    data-testid="song-next-btn"
                                    aria-label="Next song"
                                    onClick={() => skip(1)}
                                    className="absolute z-20 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center"
                                    style={{
                                        left: "76.3%",
                                        top: "79.6%",
                                    }}
                                />
                                <button
                                    data-testid="song-share-btn"
                                    aria-label="Share song"
                                    onClick={share}
                                    className="absolute z-20 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center"
                                    style={{
                                        left: "37.5%",
                                        top: "89.5%",
                                    }}
                                />
                            </div>
                            {/* play — over the artist card's baked button */}
                            <button
                                data-testid="song-play-btn"
                                aria-label={`${thisCardPlaying ? "Pause" : "Play"} ${view.title}`}
                                onClick={() => startPlay(viewIdx)}
                                className={`absolute left-1/2 z-20 flex h-[58px] w-[58px] -translate-x-1/2 -translate-y-1/2 sm:translate-y-[calc(-50%-3px)] md:translate-y-[calc(-50%-3px)] 2xl:translate-y-[calc(-50%-3px)] flex-col items-center justify-center rounded-full border transition-all duration-300 hover:scale-105 active:scale-95 ${
                                    thisCardPlaying
                                        ? "text-white"
                                        : "animate-play-pill border-white/35 bg-black/45 text-white backdrop-blur-md hover:border-white/60"
                                } ${
                                    showingPlaying
                                        ? "pointer-events-none opacity-0"
                                        : "opacity-100"
                                }`}
                                style={
                                    thisCardPlaying
                                        ? {
                                              top: "54.5%",
                                              "--pulse-rgb": glowRgb,
                                              backgroundImage: `linear-gradient(180deg, rgb(${tintLighten(glowRgb, 0.22)}) 0%, rgb(${glowRgb}) 55%, rgb(${tintDarken(glowRgb, 0.68)}) 100%)`,
                                              borderColor: `rgb(${tintDarken(glowRgb, 0.68)})`,
                                          }
                                        : {
                                              top: "54.5%",
                                              "--pulse-rgb": "255, 255, 255",
                                          }
                                }
                            >
                                <span className="text-[10px] font-bold uppercase leading-none tracking-wide translate-y-[6px]">
                                    {thisCardPlaying ? "Pause" : "Play"}
                                </span>
                                <svg
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    className="mt-[5px] h-4 w-4 translate-y-[1px]"
                                >
                                    {thisCardPlaying ? (
                                        <path d="M7 5h4v14H7zM13 5h4v14h-4z" />
                                    ) : (
                                        <path d="M8 5.14v13.72L19 12 8 5.14z" />
                                    )}
                                </svg>
                            </button>
                            {/* mini-player — way back to the playing song's full-screen player */}
                            {playIdx !== null && !showingPlaying && (
                                <div
                                    data-testid="mini-player"
                                    style={{ "--pulse-rgb": glowRgb }}
                                    className={`absolute left-[6px] right-[6px] z-20 flex h-[40px] md:h-[46px] items-center gap-2.5 overflow-hidden rounded-lg border border-white/35 bg-black/55 py-[6px] pl-1.5 pr-4 shadow-[0_4px_24px_rgba(0,0,0,0.5)] backdrop-blur-md transition-all duration-300 hover:scale-[1.02] hover:border-white/60 bottom-[calc(2.5%+40px)] md:bottom-[calc(2.5%+44px)] ${
                                        isPlaying
                                            ? "animate-play-pill-strong"
                                            : ""
                                    }`}
                                >
                                    {/* left zone — artwork + title/artist open the full-screen player */}
                                    <button
                                        type="button"
                                        data-testid="mini-player-open"
                                        aria-label={`Open player — ${PHONE_TRACKS[playIdx].title}`}
                                        onClick={() => {
                                            setViewIdx(playIdx);
                                            setPlayerOpen(true);
                                        }}
                                        className="flex min-w-0 flex-1 items-center gap-2.5"
                                    >
                                        <img
                                            src={PHONE_TRACKS[playIdx].cover}
                                            alt=""
                                            className="h-[28px] w-[28px] shrink-0 rounded-full object-cover"
                                        />
                                        <div className="flex min-w-0 flex-1 flex-col justify-center gap-[3px]">
                                            <ScrollText
                                                text={PHONE_TRACKS[playIdx].title}
                                                moving={isPlaying}
                                                className="min-w-0 text-left text-[13px] font-bold leading-none text-white"
                                            />
                                            <ScrollText
                                                text={PHONE_TRACKS[playIdx].artist}
                                                moving={isPlaying}
                                                className="min-w-0 text-left text-[11px] font-normal leading-none text-white/80"
                                            />
                                        </div>
                                    </button>
                                    {/* playing indicator — artwork-colored EQ bars, left of the transport chip */}
                                    <span
                                        aria-hidden
                                        data-testid="mini-eq"
                                        className={`flex h-[20px] shrink-0 items-end gap-[2px] transition-opacity duration-300 ${
                                            isPlaying
                                                ? "opacity-100"
                                                : "pointer-events-none opacity-0"
                                        }`}
                                    >
                                        {[0, 1, 2, 3, 4].map((i) => (
                                            <span
                                                key={i}
                                                ref={(el) => (barsRef.current[i] = el)}
                                                className="block w-[2px] rounded-sm"
                                                style={{
                                                    height: "100%",
                                                    background: `rgb(${glowRgb})`,
                                                    transformOrigin: "bottom",
                                                    transform: "scaleY(0.18)",
                                                }}
                                            />
                                        ))}
                                    </span>
                                    {/* right zone — chevron/EQ toggles play-pause in place */}
                                    <button
                                        type="button"
                                        data-testid="mini-player-toggle"
                                        aria-label={isPlaying ? "Pause song" : "Play song"}
                                        onClick={() => startPlay(playIdx)}
                                        className="flex shrink-0 items-center justify-center"
                                    >
                                        {isPlaying ? (
                                            <span className="flex h-[24px] w-[24px] shrink-0 items-center justify-center rounded-full border border-white/35">
                                                <svg
                                                    viewBox="0 0 24 24"
                                                    fill="currentColor"
                                                    className="h-4 w-4"
                                                    style={{
                                                        color: `rgb(${glowRgb})`,
                                                    }}
                                                >
                                                    <path d="M7 5h4v14H7zM13 5h4v14h-4z" />
                                                </svg>
                                            </span>
                                        ) : (
                                            <span className="flex h-[24px] w-[24px] shrink-0 items-center justify-center rounded-full border border-white/35">
                                                <svg
                                                    viewBox="0 0 24 24"
                                                    fill="currentColor"
                                                    className="h-4 w-4"
                                                    style={{
                                                        color: `rgb(${glowRgb})`,
                                                    }}
                                                >
                                                    <path d="M8 5.14v13.72L19 12 8 5.14z" />
                                                </svg>
                                            </span>
                                        )}
                                    </button>
                                </div>
                            )}
                            {/* pause/play — over the Now Playing screen's baked button */}
                            <button
                                data-testid="song-pause-btn"
                                aria-label={isPlaying ? "Pause song" : "Play song"}
                                onClick={() => startPlay(viewIdx)}
                                className={`absolute left-1/2 z-20 flex h-[58px] w-[58px] -translate-x-1/2 -translate-y-1/2 sm:translate-y-[calc(-50%+3px)] flex-col items-center justify-center rounded-full border transition-all duration-300 hover:scale-105 active:scale-95 ${
                                    isPlaying
                                        ? "text-white"
                                        : "animate-play-pill border-white/35 bg-black/45 text-white backdrop-blur-md hover:border-white/60"
                                } ${
                                    showingPlaying
                                        ? "opacity-100"
                                        : "pointer-events-none opacity-0"
                                }`}
                                style={
                                    isPlaying
                                        ? {
                                              top: "80.5%",
                                              "--pulse-rgb": glowRgb,
                                              backgroundImage: `linear-gradient(180deg, rgb(${tintLighten(glowRgb, 0.22)}) 0%, rgb(${glowRgb}) 55%, rgb(${tintDarken(glowRgb, 0.68)}) 100%)`,
                                              borderColor: `rgb(${tintDarken(glowRgb, 0.68)})`,
                                          }
                                        : {
                                              top: "80.5%",
                                              "--pulse-rgb": "255, 255, 255",
                                          }
                                }
                            >
                                <span className="text-[10px] font-bold uppercase leading-none tracking-wide translate-y-[6px]">
                                    {isPlaying ? "Pause" : "Play"}
                                </span>
                                <svg
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    className="mt-[5px] h-4 w-4 translate-y-[1px]"
                                >
                                    {isPlaying ? (
                                        <path d="M7 5h4v14H7zM13 5h4v14h-4z" />
                                    ) : (
                                        <path d="M8 5.14v13.72L19 12 8 5.14z" />
                                    )}
                                </svg>
                            </button>
                            {/* exit — collapse the full-screen player back to the artist card (song keeps playing) */}
                            <button
                                data-testid="song-return-btn"
                                aria-label="Return to artist page"
                                onClick={() => setPlayerOpen(false)}
                                className={`absolute left-[22.4%] min-[1000px]:left-[calc(22.4%-2px)] z-20 flex -translate-x-1/2 -translate-y-1/2 items-center gap-0.5 rounded-full border border-white/25 bg-black/40 py-1 pl-1.5 pr-2 text-white/85 backdrop-blur-md transition-all duration-300 hover:scale-105 hover:border-neon/70 hover:text-white active:scale-95 ${
                                    showingPlaying
                                        ? "opacity-100"
                                        : "pointer-events-none opacity-0"
                                }`}
                                style={{ top: "8.6%" }}
                            >
                                <svg
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="h-2.5 w-2.5"
                                >
                                    <path d="M6 9l6 6 6-6" />
                                </svg>
                                <span className="text-[9px] font-bold uppercase tracking-wide">
                                    Return
                                </span>
                            </button>
                            <div className="pointer-events-none absolute left-1/2 top-2.5 z-10 h-[15px] w-[62px] -translate-x-1/2 rounded-full bg-black" />
                        </div>
                    </div>
                </div>
                <div
                    className="shrink-0 overflow-hidden"
                    style={{
                        width: THUMB_W,
                        height:
                            railH ??
                            visibleCount * SLOT_H + (visibleCount - 1) * 10,
                    }}
                    data-testid="song-cover-window"
                >
                    <div
                        ref={railRef}
                        className={`flex w-full flex-col ${
                            loop ? "thumbloop-rail" : "justify-between"
                        }`}
                        style={{
                            gap: loop ? thumbGap : Math.max(railGap - 44, 2),
                            paddingBottom: loop ? thumbGap : 0,
                        }}
                    >
                        {strip.map((m, i) => {
                            const trackIdx = i % PHONE_TRACKS.length;
                            const isDup = i >= PHONE_TRACKS.length;
                            return (
                                <div
                                    key={`${m.id}-${isDup ? "b" : "a"}`}
                                    aria-hidden={isDup}
                                    className="w-full"
                                >
                                    <button
                                        data-testid={`song-cover-btn-${trackIdx}`}
                                        onClick={() => {
                                            setViewIdx(trackIdx);
                                            pauseRailBriefly();
                                        }}
                                        aria-label={`Show ${m.title}`}
                                        tabIndex={isDup ? -1 : 0}
                                        className={`aspect-square w-full overflow-hidden rounded-xl border transition-all duration-300 ${
                                            trackIdx === viewIdx
                                                ? "border-neon shadow-[0_0_18px_rgba(3,135,226,0.45)]"
                                                : "border-white/10 opacity-80 hover:opacity-100"
                                        }`}
                                    >
                                        <img
                                            src={m.cover}
                                            alt={m.title}
                                            className="block h-full w-full object-cover"
                                        />
                                    </button>
                                    <div className="-mt-1 h-[26px] overflow-hidden text-center leading-[1.1]">
                                        <ScrollText
                                            text={m.title}
                                            moving={true}
                                            className={`text-[12px] font-semibold ${
                                                trackIdx === viewIdx
                                                    ? "text-white"
                                                    : "text-white/70"
                                            }`}
                                        />
                                        <ScrollText
                                            text={m.artist}
                                            moving={
                                                isPlaying &&
                                                playIdx === trackIdx
                                            }
                                            className="text-[11px] text-white/45"
                                        />
                                    </div>
                                    <div
                                        className={`mt-[3px] flex justify-center transition-opacity duration-500 ${
                                            isPlaying && playIdx === trackIdx
                                                ? "opacity-100"
                                                : "opacity-0"
                                        }`}
                                        data-testid={`song-playing-badge-${trackIdx}`}
                                    >
                                        <span className="h-[5px] w-[5px] rounded-full bg-neon shadow-[0_0_8px_rgba(3,135,226,0.95)]" />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                </div>
                <p
                    className="relative z-20 transform-gpu sm:hidden max-sm:mt-[-13px] max-sm:ml-[78px] text-[15px] font-bold uppercase leading-snug tracking-wide text-[#8CC8FF]"
                    data-testid="audio-available-note"
                >
                    Now Available
                </p>
                <p
                    className="hidden sm:block text-center -translate-x-[45px] -translate-y-[4px] mt-3 text-[15px] font-bold uppercase tracking-wide text-[#8CC8FF]"
                    data-testid="audio-available-note-desktop"
                >
                    Now Available
                </p>
                <p
                    className="-mt-[2px] max-sm:w-full max-sm:translate-x-0 max-sm:ml-0 max-sm:text-left sm:w-[calc(100%+10px)] sm:-translate-x-[20px] sm:text-left text-[16.5px] leading-snug text-white"
                    data-testid="audio-musician-cta"
                >
                    Musicians, do you want to stand out from the slop? Sign up and create a RIVVR Profile page. Upload your music, earn more and gain real fans. Join the new platform for Artists to be curated, showcased and discovered.
                </p>
            </div>
        </div>
    );
}
